import { AgentThreadRecord } from "@/shared/domain/agentThread";
import { HttpClient, robotoHeaders, PaginatedResponse } from "@/shared/http";

import {
  AgentRecord,
  CreateAgentRequest,
  LaunchAgentRequest,
  UpdateAgentRequest,
} from "./AgentRecord";

interface Options {
  abortSignal?: AbortSignal;
  orgId?: string;
}

interface ListOptions extends Options {
  pageToken?: string;
  limit?: number;
}

/**
 * Client for the ``/v1/ai/agents`` endpoints.
 *
 * Methods are 1:1 with the backend routes; the React Query hooks in
 * ``./hooks`` wrap each method with caching/invalidation. Keep this layer
 * free of UI state - it only constructs URLs, applies headers, and parses
 * JSON.
 */
export class AgentService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async createAgent(
    request: CreateAgentRequest,
    options?: Options,
  ): Promise<AgentRecord> {
    if (!options?.orgId) {
      throw Error("createAgent requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl("v1/ai/agents");
    const response = await this.#httpClient.post(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
      body: JSON.stringify(request),
    });
    return response.json<AgentRecord>();
  }

  public async getAgent(
    agentId: string,
    options?: Options,
  ): Promise<AgentRecord> {
    // ``orgId`` is required even on the read paths: the agents router is
    // org-scoped, so multi-org users without a declared org get a 400
    // ("single-org operation"). Treat orgId as a hard precondition rather
    // than a "send if available" header to surface the misuse at the call
    // site rather than at the network boundary.
    if (!options?.orgId) {
      throw Error("getAgent requires an Org ID, none was provided");
    }
    const requestUrl = this.#httpClient.constructUrl(`v1/ai/agents/${agentId}`);
    const response = await this.#httpClient.get(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
    });
    return response.json<AgentRecord>();
  }

  public async listAgents(
    options?: ListOptions,
  ): Promise<PaginatedResponse<AgentRecord>> {
    if (!options?.orgId) {
      throw Error("listAgents requires an Org ID, none was provided");
    }

    const searchParams = new URLSearchParams();
    if (options.pageToken) {
      searchParams.append("page_token", options.pageToken);
    }
    if (options.limit !== undefined) {
      searchParams.append("limit", options.limit.toString());
    }

    const requestUrl = this.#httpClient.constructUrl(
      "v1/ai/agents",
      searchParams,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
    });
    return response.json<PaginatedResponse<AgentRecord>>();
  }

  public async updateAgent(
    agentId: string,
    request: UpdateAgentRequest,
    options?: Options,
  ): Promise<AgentRecord> {
    if (!options?.orgId) {
      throw Error("updateAgent requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl(`v1/ai/agents/${agentId}`);
    const response = await this.#httpClient.put(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
      body: JSON.stringify(request),
    });
    return response.json<AgentRecord>();
  }

  public async deleteAgent(agentId: string, options?: Options): Promise<void> {
    if (!options?.orgId) {
      throw Error("deleteAgent requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl(`v1/ai/agents/${agentId}`);
    await this.#httpClient.delete(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
    });
  }

  public async launchAgent(
    agentId: string,
    request: LaunchAgentRequest,
    options?: Options,
  ): Promise<AgentThreadRecord> {
    if (!options?.orgId) {
      throw Error("launchAgent requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/agents/${agentId}/invoke`,
    );
    const response = await this.#httpClient.post(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
      body: JSON.stringify(request),
    });
    return response.json<AgentThreadRecord>();
  }
}
